import { ResourceTableOption } from "PFApp/components/resource_table_toggle";
import { ShortlistWithScores } from "PFApp/components/resource_table_toggle/hooks/use_shortlist_items";
import { useProfilePillSectionCustomTypes } from "PFApp/hooks";
import { LoadingDots } from "PFComponents/loading_dots";
import Table from "PFComponents/table";
import { ColumnHelper } from "PFComponents/table/table.types";
import { useIsUnbiasedMode } from "PFCore/hooks";
import { useUnbiasedProfileName } from "PFCore/hooks/use_unbiased_profile_name";
import { Activity, Id, ShortlistState } from "PFTypes";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { RenderActionCell, RowSelection } from "../../../resource_table_toggle";
import { createAvailabilityColumn } from "./availability_column";
import { createCustomColumn } from "./custom_column";
import { createMatchColumn } from "./match_column";
import { createShortlistStateColumn } from "./shortlist_state_column";
import { createWmColumn } from "./wm_column";

type ShortlistsTableProps = RowSelection & {
  activity: Activity;
  tableData: ShortlistWithScores[];
  isLoading: boolean;
  isFetching: boolean;
  renderActionCell?: RenderActionCell;
  onShortlistUndo?: (shortlistId: Id) => void;
};

export const ShortlistsTable = ({
  activity,
  tableData,
  isLoading,
  isFetching,
  onRowSelectionChange,
  rowSelectionState,
  renderActionCell,
  onShortlistUndo
}: ShortlistsTableProps) => {
  const { t } = useTranslation("activities");
  const { profilePillCustomTypes, getValue } = useProfilePillSectionCustomTypes();
  const isUnbiasedMode = useIsUnbiasedMode(activity?.reviewers);
  const { getUnbiasedProfileName } = useUnbiasedProfileName();

  const columnsGenerator = useCallback(
    (columnHelper: ColumnHelper<ShortlistWithScores>) => {
      const wmColumnSize = profilePillCustomTypes.length > 0 ? 150 : 240;

      return [
        createWmColumn(columnHelper, {
          headerLabel: t("resourceTableToggle.shortlists.columns.workforceMember"),
          activity,
          isUnbiasedMode,
          getUnbiasedProfileName,
          size: wmColumnSize
        }),
        ...profilePillCustomTypes.map((customType) =>
          createCustomColumn(columnHelper, {
            id: customType.name,
            headerLabel: customType.display_as ?? "",
            getValue: (row) => getValue(row.profile.pills, customType.id)
          })
        ),
        createMatchColumn(columnHelper, { headerLabel: t("resourceTableToggle.shortlists.columns.match") }),
        createAvailabilityColumn(columnHelper, {
          headerLabel: t("resourceTableToggle.shortlists.columns.availability")
        }),
        createShortlistStateColumn(columnHelper, {
          headerLabel: t("resourceTableToggle.shortlists.columns.shortlistState"),
          activity,
          getStateLabel: (state: ShortlistState) => t(`show.shortlist.state.${state}`),
          onShortlistUndo
        }),
        ...(renderActionCell
          ? [
              columnHelper.accessor((row) => row, {
                id: "action",
                header: () => null,
                cell: (info) =>
                  renderActionCell({
                    data: info.getValue(),
                    option: ResourceTableOption.Shortlists
                  }),
                enableSorting: false
              })
            ]
          : [])
      ];
    },
    [
      profilePillCustomTypes,
      getValue,
      renderActionCell,
      activity,
      isUnbiasedMode,
      getUnbiasedProfileName,
      onShortlistUndo,
      t
    ]
  );

  if (isLoading) {
    return <LoadingDots circlesEnabled circleSize="sm" centered />;
  }

  const isSelectionEnabled = !!onRowSelectionChange;

  return (
    <>
      <Table<ShortlistWithScores>
        tableData={tableData}
        generateColumns={columnsGenerator}
        enableRowSelection={isSelectionEnabled}
        onRowSelectionChange={onRowSelectionChange}
        rowSelection={rowSelectionState}
        disableAutoResizing
        getRowId={(row) => `${row.profile.id}`}
        onRowClick={
          onRowSelectionChange ? (rowData) => onRowSelectionChange({ [rowData.profile.id]: true }) : undefined
        }
      />
      {isFetching && <LoadingDots circlesEnabled circleSize="xs" centered />}
    </>
  );
};
